import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import Modal from '../../common/Modal';

import _ from 'lodash';
import { toastr } from 'react-redux-toastr';
import DateFnsUtils from '@date-io/date-fns';
import enLocale from 'date-fns/locale/en-GB/index';
import Customer from '../../../constants/Customer';
import {
    TITLES,
    GENDERS,
    PHONE_TYPE_OPTIONS,
    REFERRAL_SOURCES_TYPE,
    CUSTOMER_DEFAULT_FIELDS,
    LEADS_DEFAULT_FIELDS,
    MARKETING_DATA_FIELD_NAMES
} from '../../../collums-constants/index';
import { isEmptyString, isValidEmail } from '../../../collums-components/helpers';
import { isValidPhone } from '../../../collums-components/helpers/validPhoneNumber';
import CustomerForm from '../../../collums-components/components/common/customerForm/CustomerForm';
import CancelContinueModal from '../../../collums-components/components/common/CancelContinueModal';
import OrganisationApi from '../../../collums-components/api/organizationApi';
import { makeStyles } from '@material-ui/core/styles';
import { modalsButtonStyles } from '../../../collums-constants/styles/stylesheets/buttonsStyles';

// TODO: disabled draggable because of focus issue on controller textfields

function EditCustomerModal(props) {
    const formData = useRef({});
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [file, setFile] = useState(null);
    const addAsLead = !!props?.customer?.isLead;
    const buttonClasses = makeStyles(modalsButtonStyles)();
    const [orgCustomerFields, setOrgCustomerFields] = useState(CUSTOMER_DEFAULT_FIELDS);
    const [orgLeadsFields, setOrgLeadsFields] = useState(LEADS_DEFAULT_FIELDS);
    const [marketingDataEnabled, setMarketingDataEnabled] = useState(false);
    const [orgSettingsLoaded, setOrgSettingsLoaded] = useState(false);

    const mergeWithDefaults = (savedFields, defaultFields) => {
        const defaultNames = new Set(defaultFields.map(f => f.name));
        const filtered = savedFields.filter(f => defaultNames.has(f.name));
        const savedNames = new Set(savedFields.map(f => f.name));
        const missingFields = defaultFields.filter(f => !savedNames.has(f.name));
        return [...filtered, ...missingFields];
    };

    // Save stays disabled until organisation field settings are loaded; reopening the modal retries a failed request.
    useEffect(() => {
        if (!props.isEditCustomerModalOpen || orgSettingsLoaded) return;
        OrganisationApi.getOrg()
            .then(org => {
                setMarketingDataEnabled(!!org.marketingDataEnabled);
                if (org.customerFields && org.customerFields.length > 0) {
                    setOrgCustomerFields(mergeWithDefaults(org.customerFields, CUSTOMER_DEFAULT_FIELDS));
                }
                if (org.leadsFields && org.leadsFields.length > 0) {
                    setOrgLeadsFields(mergeWithDefaults(org.leadsFields, LEADS_DEFAULT_FIELDS));
                }
                setOrgSettingsLoaded(true);
            })
            .catch(() => toastr.error('Could not load organisation settings. Please try again.'));
    }, [props.isEditCustomerModalOpen, orgSettingsLoaded]);

    // eslint-disable-next-line
    let inputNode = useRef(undefined);

    const save = async () => {
        if (orgSettingsLoaded && validadeForm()) {
            formData.current.postCode = formData.current.postal;
            const result = await props.persistCustomer({ ...formData.current, addAsLead: addAsLead }, file);
            if (result) {
                props.reloadCustomerList();
                props.hideEditCustomerModal();
            }
        }
    };

    const getFieldValue = fieldName => {
        if (fieldName === 'otherPhoneType') return formData.current.otherPhone?.[0]?.type;
        if (fieldName === 'referredBy') return formData.current.referredBy?.id || formData.current.referredBy;
        if (fieldName === 'country') return formData.current.country?.value;
        return formData.current[fieldName];
    };

    const validadeForm = () => {
        // Edit form shows all fields, so only the "required" flag from organisation settings matters.
        const defaults = addAsLead ? LEADS_DEFAULT_FIELDS : CUSTOMER_DEFAULT_FIELDS;
        let fields = addAsLead ? orgLeadsFields : orgCustomerFields;
        if (!marketingDataEnabled) {
            fields = fields.filter(f => !MARKETING_DATA_FIELD_NAMES.includes(f.name));
        }
        const errors = [];
        for (const field of fields) {
            if (!field.required) continue;
            // "Referred by" is disabled unless the referral source is "Friend".
            if (field.name === 'referredBy' && formData.current.referralSource !== REFERRAL_SOURCES_TYPE.FRIEND) {
                continue;
            }
            // Email and mobile formats are validated below.
            if (field.name === 'email') continue;
            if (field.name === 'mobilePhone') {
                if (isEmptyString(formData.current.mobilePhone)) errors.push('Mobile Phone is required');
                continue;
            }
            const value = getFieldValue(field.name);
            if (!value || (typeof value === 'string' && isEmptyString(value))) {
                const displayLabel = (defaults.find(f => f.name === field.name) || field).label;
                errors.push(`${displayLabel} is required.`);
            }
        }
        const isEmailRequired = fields.some(f => f.name === 'email' && f.required);
        if (
            (isEmailRequired || !isEmptyString(formData.current.email)) &&
            !isValidEmail(formData.current.email || '')
        ) {
            errors.push('Need to add a valid email');
        }
        if (!isEmptyString(formData.current.mobilePhone) && !isValidPhone(formData.current.mobilePhone || '')) {
            errors.push('Need to add a valid phone number');
        }
        if (errors.length > 0) {
            errors.forEach(err => toastr.error(err));
            return false;
        }

        if (
            !addAsLead &&
            (_.isEmpty(formData.current.country) ||
                formData.current.country.label === '' ||
                formData.current.country.value === '')
        ) {
            formData.current.country = {
                value: 'GB',
                label: 'United Kingdom'
            };
        }
        if (
            addAsLead &&
            (_.isEmpty(formData.current.country) ||
                formData.current.country.label === '' ||
                formData.current.country.value === '')
        ) {
            formData.current.country = {
                value: '',
                label: ''
            };
        }
        if (formData.current.blockOnlineBooking && !formData.current.blockOnlineText?.trim()) {
            toastr.error('Need to add a text to show when online booking is blocked.');
            return false;
        }
        return true;
    };

    const cancel = () => {
        if (!_.isEqual(_.omit(formData.current, ['dateOfBirth']), _.omit(Customer(), ['dateOfBirth']))) {
            setIsConfirmModalOpen(true);
            return;
        }

        props.hideEditCustomerModal();
    };

    return (
        <>
            <Modal
                isOpen={props.isEditCustomerModalOpen}
                title={`Edit ${addAsLead ? 'lead' : 'client'}`}
                size="md"
                confirmLabel="Save"
                onCancel={cancel}
                onConfirm={save}
                disableConfirm={!orgSettingsLoaded}
                onClose={cancel}
                cancelClass={buttonClasses.cancelButton}
                confirmClass={buttonClasses.confirmButton}
            >
                <CustomerForm
                    DateFnsUtils={DateFnsUtils}
                    enLocale={enLocale}
                    formData={formData}
                    inputNode={inputNode}
                    TITLES={TITLES}
                    GENDERS={GENDERS}
                    setFile={setFile}
                    PHONE_TYPE_OPTIONS={PHONE_TYPE_OPTIONS}
                    REFERRAL_SOURCES_TYPE={REFERRAL_SOURCES_TYPE}
                    customer={props.customer}
                    deletedPhoto={props.deletedPhoto}
                    setDeletedPhoto={props.setDeletedPhoto}
                    addAsLead={addAsLead}
                />
                {isConfirmModalOpen && (
                    <CancelContinueModal
                        setOpen={setIsConfirmModalOpen}
                        onContinue={() => {
                            props.hideEditCustomerModal();
                        }}
                        closeOnBlur={false}
                        title="Your changes will not be saved"
                        contentText="Are you sure you want to continue?"
                    />
                )}
            </Modal>
        </>
    );
}

EditCustomerModal.propTypes = {
    isEditCustomerModalOpen: PropTypes.bool.isRequired,
    hideEditCustomerModal: PropTypes.func.isRequired,
    persistCustomer: PropTypes.func.isRequired,
    reloadCustomerList: PropTypes.func.isRequired,
    customer: PropTypes.object.isRequired,
    deletedPhoto: PropTypes.bool,
    setDeletedPhoto: PropTypes.func
};

export default EditCustomerModal;
